/**
 * Frontend mirror of the FROZEN Sniper contract
 * (`arbitrage-core/docs/contracts/sniper-contract.md`). Kept in sync manually —
 * the frontend never imports from the backend workspace. The camelCase keys +
 * enum literals here MUST match the control-plane TS (`sniper.types.ts`), the
 * Rust engine (`sniper_types.rs`), and the WS/REST payloads byte-for-byte.
 *
 * If the contract's enums / `SniperMarket` / `SniperNotificationFrame` change,
 * update here too. There is NO client-side score fabrication — the `MatchState`
 * (score/corners/clock/VAR) is authored by the BACKEND and delivered over the
 * `match_state` WS event + `GET /match-events/state/:fixtureId` backfill; the FE
 * renders it and derives nothing (ARMED is `count == floor(line)` over that
 * backend state).
 */

// ─── enums (string-literal unions — snake_case on the wire) ─────────────────

/** The venue a Sniper market targets. Each real market on both venues = 2 rows. */
export type SniperVenue = 'kalshi' | 'polymarket';

/** The totals counter this market resolves on (soccer V1). */
export type SniperCounter =
  | 'match_goals'
  | 'team_goals'
  | 'both_teams_to_score'
  | 'first_team_to_score'
  | 'match_corners'
  | 'team_corners';

/** Team scope — REQUIRED iff `counter` is `team_*`, otherwise `null`. */
export type SniperTeam = 'home' | 'away';

/**
 * Per-half scope for a GOALS market. `full` = the whole match (default). A
 * `first_half`/`second_half` market resolves only on goals scored in that half.
 * CORNERS are full-match only — `period` must stay `full` for `*_corners`
 * (validated client-side to mirror the server's 400).
 */
export type SniperPeriod = 'full' | 'first_half' | 'second_half';

/** All counters in display order. */
export const SNIPER_COUNTERS: SniperCounter[] = [
  'match_goals',
  'team_goals',
  'both_teams_to_score',
  'first_team_to_score',
  'match_corners',
  'team_corners',
];

// ─── persisted market (Mongo `sniper_markets`, minus Mongo internals) ────────

/** The durable operator-authored market row. `_id`/`sniperGroupId` = ObjectId strings. */
export interface SniperMarket {
  _id: string;
  sniperGroupId: string;
  fixtureId: number;
  venue: SniperVenue;
  /** Kalshi ticker | Polymarket tokenId (the Over/YES token). */
  marketId: string;
  /** Human label from the scrape (e.g. "Total Goals Over 2.5"). */
  marketLabel: string;
  counter: SniperCounter;
  /** REQUIRED iff `counter` is `team_*`; `null` for `match_*`. */
  team: SniperTeam | null;
  /** Per-half scope (goals only). `full` by default; `*_corners` stay `full`. */
  period: SniperPeriod;
  /** Half-integer (X.5), validated server-side at config time. */
  line: number;
  /** Fixed in V1 — Sniper only ever buys Over/YES. */
  buySide: 'over';
  /** Contracts to sweep. */
  size: number;
  /** 0..1, per-market (defaulted from the event-type default). */
  confidenceGate: number;
  /** Dollars — worst per-contract price for the FAK sweep. */
  maxPrice: number;
  /**
   * Dollars — limit of the GTC SELL posted to exit the sweep when the goal is
   * disallowed by VAR. `null` on rows written before the field existed.
   */
  varExitPrice: number | null;
  /**
   * Auto-NO period-close tail: at period close, sweep the opposite (No/Under)
   * side of a market that is one increment from resolving. Default false.
   */
  autoNoEnabled: boolean;
  /** Dollars — worst per-contract price the auto-NO sweep may pay. `null` = unset. */
  noMaxPrice: number | null;
  /**
   * Contracts for the auto-NO sweep, independent of the main `size`. `null`
   * inherits `size` (resolved by the Rust engine) — NOT zero, NOT a hardcoded
   * default.
   */
  noSize: number | null;
  /**
   * Polymarket Under/No tokenId the auto-NO sweep targets. Auto-filled for
   * Polymarket, empty/editable for Kalshi (Kalshi NO is native — no id needed).
   */
  noMarketId: string | null;
  /** Whether the auto-NO tail also covers extra time. Default false. */
  autoNoIncludeExtraTime: boolean;
  /**
   * Master switch for the two-phase VAR defense (Phase 1 break-even preventive
   * sell + Phase 2 aggressive-sell-to-floor). Default true. `null` on rows
   * written before the field existed.
   */
  varDefenseEnabled: boolean | null;
  /**
   * Phase 1 — post a break-even preventive sell at our buy price when a VAR
   * follows our goal. Default true. `null` on legacy rows.
   */
  varPreventiveExitEnabled: boolean | null;
  /**
   * Phase 2 — dollars — the aggressive-sell floor the disallowed-goal position
   * is dumped down to once the annulment is confirmed (a price in (0,1], like
   * `varExitPrice`). Default 0.90. `null` on legacy rows.
   */
  varConfirmedFloorPrice: number | null;
  /**
   * All-clear rebuy — restore the position if the VAR ultimately CONFIRMS the
   * goal. Default true. `null` on legacy rows.
   */
  varRebuyEnabled: boolean | null;
  /** Default false (fail-safe OFF). */
  enabled: boolean;
  /** Room for a future 'trading' strategy. */
  strategy: 'sniper';
}

// ─── engine-read shape (the `sniper:config:<fixtureId>` array element) ───────

/** What the Rust engine reads out of `sniper:config:<fixtureId>`. */
export interface SniperMarketConfig {
  /** The `sniper_markets` row `_id` (16-char prefix → COID namespace). */
  marketRuleId: string;
  fixtureId: number;
  venue: SniperVenue;
  marketId: string;
  marketLabel: string;
  counter: SniperCounter;
  team: SniperTeam | null;
  /** Per-half scope (goals only). `full` by default; `*_corners` stay `full`. */
  period: SniperPeriod;
  line: number;
  buySide: 'over';
  size: number;
  confidenceGate: number;
  maxPrice: number;
  varExitPrice: number | null;
  /** Auto-NO period-close tail (see `SniperMarket`). */
  autoNoEnabled: boolean;
  noMaxPrice: number | null;
  noSize: number | null;
  noMarketId: string | null;
  autoNoIncludeExtraTime: boolean;
  varDefenseEnabled: boolean | null;
  varPreventiveExitEnabled: boolean | null;
  varConfirmedFloorPrice: number | null;
  varRebuyEnabled: boolean | null;
  enabled: boolean;
  strategy: 'sniper';
}

// ─── live match state (per active fixture, from the classified feed) ─────────

/**
 * Per-fixture live state. Mutable — always the newest high-confidence reading;
 * a VAR revert lowers a counter (a previously-armed `2.5` becomes un-armed).
 * AUTHORED BY THE BACKEND (confidence-gated + VAR-aware) and delivered to the FE
 * over the `match_state` WS event + `GET /match-events/state/:fixtureId`
 * backfill. The FE renders it; it never derives it from raw incidents.
 */
export interface MatchState {
  homeScore: number;
  awayScore: number;
  homeCorners: number;
  awayCorners: number;
  /** Game clock seconds, or null. */
  clock: number | null;
  /** Body.Period.Id, or null. */
  period: number | null;
  /** A VAR (Id 1074) review is in progress. */
  lastVarActive: boolean;
}

/**
 * The backend-authored `match_state` envelope — IDENTICAL on the WS `match_state`
 * event and the `GET /match-events/state/:fixtureId` REST backfill. Single source
 * of truth for the payload shape (both the hook and the REST fetcher reference
 * this, no drift).
 *
 * `ts` is pinned to **ISO-8601 UTC** so it is safe to compare numerically via
 * `Date.parse(...)`. NEVER order two `ts` values with lexicographic `<` — that is
 * only correct for a fixed-width canonical string, which the contract does not
 * otherwise guarantee.
 */
export interface MatchStatePayload {
  fixtureId: number;
  state: MatchState;
  /** ISO-8601 UTC engine emit time. Compare via `Date.parse`, never string `<`. */
  ts: string;
}

// ─── notifications (the `sniper:notifications` stream `data` payload) ─────────

export type SniperNotificationType =
  | 'sniper_fired'
  | 'sniper_auto_no_fired'
  | 'sniper_var_reversal'
  | 'sniper_var_rebuy'
  | 'sniper_fire_blocked';

export interface SniperNotificationFrame {
  type: SniperNotificationType;
  fixtureId: number;
  /** Venue market id the sweep targeted. */
  marketId: string;
  venue: SniperVenue;
  /** Contracts requested (config size). */
  sizeRequested: number;
  /** Contracts actually filled (0 for `sniper_fire_blocked`). */
  filled: number;
  /** Avg per-contract fill price, or null when nothing filled. */
  avgPrice: number | null;
  /** The triggering incident (dedup key). */
  incidentKey: string;
  /** fire_blocked / var_reversal detail (e.g. "book_unavailable"). */
  reason?: string;
  /** ISO 8601, engine emit time. */
  ts: string;
}

// ─── control-plane REST shapes (prediction-manager `/sniper/*`) ──────────────

/**
 * One event (match) nested under a Sniper group. Extends the activatable-match
 * row (reused `activatable_matches`) with the group ref + its `sniper_markets`.
 * The FE renders the live state line from the match-events feed, the Active
 * toggle from `active`, and the market rows from `markets`.
 */
export interface SniperEvent {
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
  league?: string | null;
  startDate?: string | null;
  sniperGroupId: string;
  active: boolean;
  /** Optional live-health hint the backend may attach (Redis-derived). */
  live?: boolean;
  /** The event's persisted Sniper markets (nested in `GET /sniper/groups`). */
  markets: SniperMarket[];
  /** Count fallback when `markets` is omitted by the backend. */
  marketCount?: number;
}

/**
 * The account a running Sniper group trades against. `test` = the separate
 * low-balance TEST account; `normal` = the live real-money account.
 */
export type SniperGroupMode = 'test' | 'normal';

/** A Sniper group — operator-defined bucket of events + their markets. */
export interface SniperGroup {
  _id: string;
  name: string;
  sport?: string;
  events: SniperEvent[];
  /** Per-group run state (from `GET /sniper/groups`, backend PR #200). */
  running: boolean;
  /** The mode the group was Started in, or `null` while stopped. */
  mode: SniperGroupMode | null;
  /** Whether the group is currently halted by a panic. */
  panicked: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Body for `POST /sniper/groups`. */
export interface CreateSniperGroupDto {
  name: string;
  sport: string;
}

/** Body for `POST /sniper/groups/:id/start`. */
export interface StartSniperGroupDto {
  mode: SniperGroupMode;
}

/** Body for `POST /sniper/groups/:id/events` (upsert activatable_match + link). */
export interface UpsertSniperEventDto {
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
  league?: string | null;
  startDate?: string | null;
}

/** Body for `POST /sniper/events/:fixtureId/import`. */
export interface ImportSniperMarketsDto {
  kalshiEventUrl?: string;
  polymarketEventUrl?: string;
  /** Team names passed to the guesser so it can auto-fill team-goal markets. */
  homeTeam?: string;
  awayTeam?: string;
}

/**
 * A scraped candidate returned by `POST /sniper/events/:fixtureId/import`. The
 * backend auto-GUESSES `counter`/`team`/`line` from the Kalshi ticker / Poly
 * subtitle; the operator reviews + confirms. `null` guesses surface as
 * "unresolved" and MUST be filled before persisting.
 */
export interface SniperMarketCandidate {
  venue: SniperVenue;
  marketId: string;
  marketLabel: string;
  // The backend nests the auto-guess under `guess`; older shapes returned the
  // fields flat. Both are accepted (see `candidateToDraft`).
  guess?: {
    counter: SniperCounter | null;
    team: SniperTeam | null;
    line: number | null;
    /** Per-half scope guess (goals only); absent/`full` for match-wide totals. */
    period?: SniperPeriod | null;
  } | null;
  counter?: SniperCounter | null;
  team?: SniperTeam | null;
  line?: number | null;
  /**
   * Polymarket Under/No tokenId — TOP-LEVEL on the candidate (never in `guess`).
   * The scraper carries the Poly No token so the auto-NO draft can prefill it;
   * absent for Kalshi (native NO).
   */
  noMarketId?: string | null;
}

/**
 * One editable row in the import-review step. `counter`/`line` may be UNSET
 * (`null`) while the operator resolves an unclassified candidate — such a draft
 * is INCOMPLETE (`isDraftComplete`) and is NEVER persisted. Only complete
 * drafts (counter/line non-null) are sent in the
 * `POST /sniper/events/:fixtureId/markets` body.
 */
export interface SniperMarketDraft {
  venue: SniperVenue;
  marketId: string;
  marketLabel: string;
  counter: SniperCounter | null;
  team: SniperTeam | null;
  /** Per-half scope (goals only). `full` by default; `*_corners` stay `full`. */
  period: SniperPeriod;
  line: number | null;
  buySide: 'over';
  size: number;
  confidenceGate: number;
  maxPrice: number;
  varExitPrice: number;
  /** Auto-NO period-close tail (see `SniperMarket`). Defaults: off, 0.99, prefilled/null, off. */
  autoNoEnabled: boolean;
  noMaxPrice: number | null;
  noMarketId: string | null;
  autoNoIncludeExtraTime: boolean;
  /** Master switch for the two-phase VAR defense. Default true. */
  varDefenseEnabled: boolean;
  /** Phase 1 — break-even preventive sell at our buy price. Default true. */
  varPreventiveExitEnabled: boolean;
  /** Phase 2 — aggressive-sell floor, a price in (0,1]. Default 0.90. */
  varConfirmedFloorPrice: number;
  /** All-clear rebuy if the VAR confirms the goal. Default true. */
  varRebuyEnabled: boolean;
  enabled: boolean;
}

/** Body for `POST /sniper/events/:fixtureId/markets`. */
export interface CreateSniperMarketsDto {
  markets: SniperMarketDraft[];
}

/** Partial body for `PATCH /sniper/markets/:id`. */
export interface PatchSniperMarketDto {
  size?: number;
  maxPrice?: number;
  varExitPrice?: number;
  varDefenseEnabled?: boolean;
  varPreventiveExitEnabled?: boolean;
  varConfirmedFloorPrice?: number;
  varRebuyEnabled?: boolean;
  confidenceGate?: number;
  line?: number;
  counter?: SniperCounter;
  team?: SniperTeam | null;
  period?: SniperPeriod;
  /** Auto-NO period-close tail (see `SniperMarket`). */
  autoNoEnabled?: boolean;
  noMaxPrice?: number | null;
  noSize?: number | null;
  noMarketId?: string | null;
  autoNoIncludeExtraTime?: boolean;
  enabled?: boolean;
}
